import { useAuthContext } from './use-auth-context.hook';
import AuthClientStore from '../services/auth-client.service';
import { ApiMethod } from '../models/api-types.type';

const BASE = '/services/pms/assistant';

export interface AssistantTable {
    title?: string;
    columns: string[];
    rows: string[][];
    /** Where the figures came from and when. */
    note?: string;
}

/**
 * Generative UI: the parts of a reply that are drawn, not written.
 *
 * Built on the server — from data it read, or from a presentation tool whose
 * arguments it validated (pms-assistant/ui-blocks.ts) — so every block arrives
 * in one of these shapes. The panel draws each from a fixed registry; nothing
 * here is markup.
 */
export type UiAction =
    | { kind: 'ask'; label: string; message: string; id?: string }
    | { kind: 'open'; label: string; route: string; id?: string }
    | { kind: 'copy'; label: string; text: string };

export type ChecklistStatus = 'done' | 'todo' | 'attention' | 'blocked';
export type PreviewKind = 'shopping_assistant' | 'visualizer_button';
export type ChartKind = 'line' | 'bar' | 'hbar' | 'donut';

export type UiBlock = UiShape & {
    /** Set on blocks the server built: the key their title is translated by. */
    titleKey?: string;
};

type UiShape =
    | { type: 'checklist'; title: string; goal?: string; steps: ChecklistStep[] }
    | { type: 'code'; title?: string; language: string; code: string; note?: string }
    | { type: 'actions'; prompt?: string; actions: UiAction[] }
    | { type: 'stats'; title?: string; items: Array<{ id?: string; label: string; value: string; change?: number; tone?: 'good' | 'risk' | 'neutral'; hint?: string }>; note?: string }
    | { type: 'chart'; title?: string; kind: ChartKind; unit?: string; series: Array<{ name: string; points: Array<{ x: string; y: number }> }>; note?: string }
    | {
          type: 'cards';
          title?: string;
          items: Array<{
              title: string;
              subtitle?: string;
              image?: string;
              badge?: string;
              tone?: 'good' | 'risk' | 'neutral';
              facts?: Array<{ id?: string; label: string; value: string }>;
              share?: number;
              href?: string;
              action?: UiAction;
          }>;
          note?: string;
      }
    | { type: 'products'; title?: string; items: Array<{ uuid: string; name: string; sku?: string; image?: string; kind?: string; visible?: boolean; ready?: boolean; missing?: string[] }> }
    | { type: 'preview'; kind: PreviewKind; settings: Record<string, any>; title?: string }
    | { type: 'status'; title?: string; items: Array<Worded & { key: string; label: string; state: 'done' | 'attention' | 'missing' | 'locked'; action?: UiAction }> };

/** Server-built words: English `detail`, plus the key and figures the panel translates it by. */
export interface Worded {
    detail?: string;
    detailKey?: string;
    params?: Record<string, string | number>;
}

export interface ChecklistStep extends Worded {
    id: string;
    label: string;
    status: ChecklistStatus;
    optional?: boolean;
    action?: UiAction;
}

/** How a proposed change will look — drawn beside its form and redrawn as the form is edited. */
export interface ProposalPreview {
    kind: PreviewKind;
    settings: Record<string, any>;
    /** Form field path → the preview setting it moves. */
    bind: Record<string, string>;
}

export interface AssistantMessage {
    role: 'vendor' | 'assistant';
    text: string;
    toolsUsed?: string[];
    /**
     * Pictures that belong with this message.
     *
     * On a vendor message these are what they attached; on a reply they are what
     * the assistant found — generated images, a product's gallery — and the panel
     * shows them rather than listing URLs.
     */
    images?: string[];
    /**
     * Pages offered under a reply, for the part of a job the assistant cannot
     * do itself — connecting a social account, uploading a 3D model. The panel
     * renders one button each; nothing navigates on its own.
     */
    links?: Array<{ key: string; route: string; label: string }>;
    /**
     * Grids drawn under the reply, for an answer whose shape is a comparison.
     *
     * Cells arrive as strings so the assistant decides how a figure reads —
     * "1,240" or "€1.2k" — rather than the panel guessing at a locale.
     */
    tables?: AssistantTable[];
    /**
     * The specialists this reply sent work to — pointers to their runs, with
     * the one line each came back with. The runs hold the steps and reports.
     */
    team?: AssistantTeamRun[];
    /** Checklists, charts, cards, previews, quick replies — see UiBlock. */
    blocks?: UiBlock[];
    at: string;
}

export interface AssistantTeamRun {
    runId: string;
    agent: string;
    name: string;
    brief: string;
    status: 'succeeded' | 'failed';
    headline?: string;
}

/** One agent on the vendor's team, as the Agents page reads it. */
export interface TeamRosterEntry {
    id: string;
    kind: 'lead' | 'specialist' | 'always-on' | 'worker';
    name?: string;
    role?: string;
    tools: string[];
    available: boolean;
    running: number;
    thisWeek: number;
    last: {
        runId: string;
        status: string;
        at: string;
        brief: string;
        headline?: string;
        result?: Record<string, any>;
    } | null;
}

/**
 * What approving does to the world. The same classes the studio agent uses,
 * so one rule governs every proposal: a `write` changes the shop, a `spend`
 * costs credits and is quoted, a `publish` leaves the shop and is worded as
 * such. Absent on older proposals, which were all writes.
 */
export type ProposalSideEffect = 'write' | 'spend' | 'publish';

/**
 * One editable part of a proposed change.
 *
 * Derived on the server from the arguments the assistant actually proposed, so
 * a proposal to rewrite a description carries a Description box and nothing
 * else. `path` is the only thing the panel sends back — the server recomputes
 * which paths were on offer and refuses the rest.
 */
export type ProposalFieldKind =
    | 'text'
    | 'textarea'
    | 'number'
    | 'boolean'
    | 'color'
    | 'select'
    | 'list';

export interface ProposalField {
    path: string;
    /** The bare argument name, for translating the labels we know. */
    key: string;
    label: string;
    kind: ProposalFieldKind;
    value: unknown;
    options?: string[];
    hint?: string;
}

export interface PendingAction {
    actionId: string;
    tool: string;
    args: Record<string, unknown>;
    summary: string;
    /** Absent when the change has nothing a vendor could sensibly edit. */
    fields?: ProposalField[];
    sideEffect?: ProposalSideEffect;
    creditCost?: number;
    status: 'pending' | 'applied' | 'declined';
    proposedAt: string;
    resolvedAt?: string;
    preview?: ProposalPreview;
    /** What approving it led to: the next steps offered, when it was one step of a longer job. */
    outcome?: { runId?: string; followUps?: UiAction[] };
}

/** A pending proposal with the conversation it belongs to — the Work page's row. */
export interface PendingActionSummary {
    threadUuid: string;
    threadTitle?: string;
    actionId: string;
    tool: string;
    summary: string;
    sideEffect?: ProposalSideEffect;
    creditCost?: number;
    proposedAt: string;
}

export interface AssistantThread {
    uuid: string;
    title: string;
    messages: AssistantMessage[];
    actions: PendingAction[];
    lastMessageAt: string;
}

export type ThreadSummary = Pick<AssistantThread, 'uuid' | 'title' | 'lastMessageAt'>;

/** What applying a change produced, and where the vendor continues if it is not finished. */
export interface AppliedAction {
    applied: boolean;
    tool: string;
    result?: Record<string, any>;
    followUps?: UiAction[];
    next?: {
        route: string;
        /** What the destination should start from, when applying is a handover. */
        handoff?: { brief?: string; productUuid?: string; referenceUrl?: string };
    };
}

/** What the stream reports as it happens. */
export interface AssistantEvent {
    type: 'thinking' | 'tool_call' | 'tool_result' | 'done' | 'error' | 'usage' | 'team';
    data: Record<string, any>;
}

export const useAssistantApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    /**
     * Ask, and report back as it goes.
     *
     * Uses `fetch` rather than the shared axios helper because this response is
     * a stream: axios resolves once, with the whole body, which would leave the
     * vendor watching a spinner for however long the agent takes and then show
     * everything at once.
     *
     * `onEvent` is called for each event; the promise resolves when the stream
     * closes. Aborting is the caller's job — pass a signal.
     */
    const ask = async (
        /** `page` is where the vendor is, so "this" can mean what is on it. */
        body: { threadUuid?: string; message: string; images?: string[]; page?: string },
        onEvent: (event: AssistantEvent) => void,
        signal?: AbortSignal,
    ): Promise<void> => {
        const response = await fetch(`${process.env.REACT_APP_PMS_API_URL}${BASE}/ask`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${AuthClientStore.getAccessToken() ?? ''}`,
            },
            body: JSON.stringify(body),
            signal,
        });

        if (!response.ok || !response.body) {
            throw new Error(`The assistant could not be reached (${response.status}).`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // SSE frames are separated by a blank line, and a chunk can split one
        // anywhere — including mid-JSON. Everything after the last separator
        // stays in the buffer until the rest of it arrives.
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split('\n\n');
            buffer = frames.pop() ?? '';

            for (const frame of frames) {
                const line = frame.trim();
                if (!line.startsWith('data:')) continue;

                const payload = line.slice(5).trim();
                if (payload === '[DONE]') return;

                try {
                    onEvent(JSON.parse(payload));
                } catch {
                    // A frame we cannot parse is not worth ending the stream
                    // over — the next one is probably fine.
                }
            }
        }
    };

    /**
     * Put an image somewhere the assistant can see it.
     *
     * The model is given a URL, not the bytes, so the file has to be uploaded
     * before the question is asked. Multipart through fetch rather than the
     * axios helper: setting Content-Type by hand on a FormData breaks the
     * boundary, and the helper sets it.
     */
    const uploadImage = async (file: File): Promise<{ url: string }> => {
        const form = new FormData();
        form.append('image', file);

        const response = await fetch(`${process.env.REACT_APP_PMS_API_URL}${BASE}/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${AuthClientStore.getAccessToken() ?? ''}` },
            body: form,
        });

        if (!response.ok) {
            const detail = await response.json().catch(() => null);
            throw new Error(detail?.message ?? 'That image could not be uploaded.');
        }

        return response.json();
    };

    const listThreads = async (): Promise<ThreadSummary[]> => {
        const response: any = await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/threads`);
        return response?.data ?? response ?? [];
    };

    /** Every proposal still waiting, across all conversations. */
    const listPendingActions = async (): Promise<PendingActionSummary[]> => {
        const response: any = await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/actions`);
        return response?.data ?? response ?? [];
    };

    const getThread = async (uuid: string): Promise<AssistantThread> => {
        const response: any = await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/threads/${uuid}`);
        return response?.data ?? response;
    };

    /**
     * Delete a conversation, with everything recorded in it.
     *
     * Applied changes are untouched — they live in the catalogue or the brand
     * kit, wherever they landed. What goes is the account of how they got
     * there, and any proposal still waiting.
     */
    const removeThread = async (uuid: string): Promise<{ removed: boolean; pendingDropped: number }> => {
        const response: any = await sendAuthGuardedRequest(ApiMethod.DELETE, `${BASE}/threads/${uuid}`);
        return response?.data ?? response;
    };

    const applyAction = async (
        threadUuid: string,
        actionId: string,
        /** What the vendor changed in the form, keyed by field path. */
        edits: Record<string, unknown> = {},
    ): Promise<AppliedAction> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            `${BASE}/threads/${threadUuid}/actions/${actionId}/apply`,
            { edits },
        );
        return response?.data ?? response;
    };

    /** Every agent on the team, what each last did, and what is running. */
    const getTeam = async (): Promise<TeamRosterEntry[]> => {
        const response: any = await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/team`);
        return (response?.data ?? response)?.members ?? [];
    };

    const declineAction = async (threadUuid: string, actionId: string) => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            `${BASE}/threads/${threadUuid}/actions/${actionId}/decline`,
            {},
        );
        return response?.data ?? response;
    };

    return {
        ask,
        uploadImage,
        listThreads,
        listPendingActions,
        getThread,
        removeThread,
        applyAction,
        declineAction,
        getTeam,
    };
};
